import React, { useEffect, useMemo, useRef, useState } from "react";

import TextView from "../../../components/TextView";
import { colors } from "../../../utils";
import {
  segmentOptions,
  defaultSegmentId,
  truncateSegmentName,
  NO_SEGMENTS_MESSAGE,
} from "../segmentPicker.helpers";
import {
  PickerBackdrop,
  PickerPanel,
  PickerHeader,
  PickerBody,
  PickerFooter,
  SegmentList,
  SegmentOption,
  SegmentRadioDot,
  SegmentColorSwatch,
  PickerButton,
  PickerGhostButton,
  PickerNotice,
} from "./SegmentPickerDialog.styles";

const TITLE_ID = "segment-picker-title";
const DESC_ID = "segment-picker-desc";

/**
 * "Add to Segment" — pick which Segment(s) a captured item goes into.
 *
 * Kelly (VP Sales), QA-52/QA-53, verbatim: "right underneath the captured item
 * it should say 'add to segment'. And if there are a bunch of segments already
 * in the wheel all of the segments should drop down and you get to pick the
 * segment you want to drop it in."
 *
 * QA-1096 (Gordon-decided BUILD, 2026-08-28 brainstorm batch): the same
 * capture can go into MORE than one Segment in a single action, so this is a
 * multi-select CHECKBOX GROUP (`role="group"` of `role="checkbox"` options),
 * not the mutually-exclusive radiogroup it started as. Arrow keys still move
 * a roving focus across the list (so a long list stays fast to scan by
 * keyboard), but — unlike a radiogroup — moving focus no longer changes what
 * is checked; only Space/Enter/click toggles an option, which is what
 * distinguishes a checkbox group from a radiogroup for assistive tech.
 *
 * This is NOT a second-class fallback for drag-and-drop. Drag is unreachable by
 * keyboard and by screen reader, and is awkward on touch — so this dialog is
 * the accessible path and the primary mobile one.
 *
 * States designed here, not just the happy path:
 *   EMPTY   — a wheel with no segments SAYS so and offers no dead-end picker.
 *   ONE     — a single option, still preselected, still explicitly confirmed.
 *   MANY    — the list scrolls inside its own container; the panel never grows
 *             past the viewport. Any subset (including all) may be checked.
 *   LONG    — a long segment name truncates visually while the FULL name stays
 *             in the accessible label.
 *   BUSY    — the confirm button reports progress and cannot be double-fired.
 *   ERROR   — the failure is stated in the dialog, which stays open so the user
 *             can retry without losing their selection.
 */
const SegmentPickerDialog = ({
  isOpen = false,
  onClose,
  onConfirm,
  wheelSections = [],
  captureLabel = "capture",
  isBusy = false,
  errorMessage = null,
}) => {
  const options = useMemo(() => segmentOptions(wheelSections), [wheelSections]);
  const [selectedIds, setSelectedIds] = useState(() => {
    const first = defaultSegmentId(wheelSections);
    return first === null ? new Set() : new Set([first]);
  });
  const [focusedIndex, setFocusedIndex] = useState(0);
  const panelRef = useRef(null);
  const firstOptionRef = useRef(null);
  const closeRef = useRef(null);

  // Reopening always returns to the wheel's real first segment, so a stale
  // selection from a previous open can never be submitted against a wheel whose
  // segments have since changed.
  useEffect(() => {
    if (!isOpen) return;
    const first = defaultSegmentId(wheelSections);
    setSelectedIds(first === null ? new Set() : new Set([first]));
    setFocusedIndex(0);
  }, [isOpen, wheelSections]);

  // Escape closes; focus moves into the dialog on open (the first option when
  // there is one, otherwise the close button — never nowhere).
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => {
      (firstOptionRef.current || closeRef.current)?.focus();
    }, 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isEmpty = options.length === 0;

  const toggleOption = (index) => {
    const id = options[index].id;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setFocusedIndex(index);
  };

  /**
   * Roving focus across the checkbox group. Arrow keys move WHICH option has
   * focus — they never change what is checked, that is what makes this a
   * checkbox group instead of a radiogroup. Space/Enter toggles the focused
   * option, matching the standard checkbox keyboard contract.
   */
  const onOptionKeyDown = (e, index) => {
    const last = options.length - 1;
    let next = null;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") next = index === last ? 0 : index + 1;
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = last;
    if (next !== null) {
      e.preventDefault();
      setFocusedIndex(next);
      const node = panelRef.current?.querySelectorAll("[role='checkbox']")[next];
      node?.focus();
      return;
    }
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      toggleOption(index);
    }
  };

  const selectedCount = selectedIds.size;
  const confirmLabel = isBusy
    ? "Adding…"
    : selectedCount > 1
      ? `Add to ${selectedCount} Segments`
      : "Add to Segment";

  return (
    <>
      <PickerBackdrop onClick={onClose} data-testid="segment-picker-backdrop" />
      <PickerPanel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={DESC_ID}
        data-testid="segment-picker"
        data-state={isEmpty ? "empty" : "populated"}
      >
        <PickerHeader>
          <TextView variant="subtitle" color={colors.black} id={TITLE_ID}>
            Add to Segment
          </TextView>
          <TextView variant="text" color={colors.textGrey} id={DESC_ID}>
            {isEmpty
              ? "Nowhere to add this yet"
              : `Choose one or more Segments to add this ${captureLabel} to. It will be added at the bottom of each Segment you choose.`}
          </TextView>
        </PickerHeader>

        <PickerBody>
          {isEmpty ? (
            <PickerNotice role="note" data-testid="segment-picker-empty">
              {NO_SEGMENTS_MESSAGE}
            </PickerNotice>
          ) : (
            <SegmentList
              role="group"
              aria-labelledby={TITLE_ID}
              data-testid="segment-picker-list"
            >
              {options.map((option, index) => {
                const checked = selectedIds.has(option.id);
                return (
                  <SegmentOption
                    key={option.id}
                    ref={index === 0 ? firstOptionRef : undefined}
                    type="button"
                    role="checkbox"
                    aria-checked={checked}
                    $checked={checked}
                    tabIndex={index === focusedIndex ? 0 : -1}
                    // The FULL name, never the truncated one — a screen-reader
                    // user must not be handed the visually-cut string.
                    aria-label={
                      index === 0 ? `${option.name} (first Segment)` : option.name
                    }
                    onClick={() => toggleOption(index)}
                    onKeyDown={(e) => onOptionKeyDown(e, index)}
                    data-segment-id={option.id}
                  >
                    {/* Never a colour-only signal: the dot is a filled ring AND
                        aria-checked carries the state for assistive tech. */}
                    <SegmentRadioDot $checked={checked} aria-hidden="true" />
                    {/* QA-1095: the segment's own colour, matching its wheel
                        wedge. Decorative — the text label carries identity. */}
                    <SegmentColorSwatch
                      $color={option.color}
                      aria-hidden="true"
                      data-testid="segment-color-swatch"
                      data-color={option.color || ""}
                    />
                    <span title={option.name}>{truncateSegmentName(option.name)}</span>
                  </SegmentOption>
                );
              })}
            </SegmentList>
          )}

          {errorMessage && (
            <PickerNotice role="alert" $error data-testid="segment-picker-error">
              {errorMessage}
            </PickerNotice>
          )}
        </PickerBody>

        <PickerFooter>
          <PickerGhostButton ref={closeRef} type="button" onClick={onClose}>
            Cancel
          </PickerGhostButton>
          <PickerButton
            type="button"
            disabled={isEmpty || selectedCount === 0 || isBusy}
            onClick={() => onConfirm?.(Array.from(selectedIds))}
            data-testid="segment-picker-confirm"
          >
            {confirmLabel}
          </PickerButton>
        </PickerFooter>
      </PickerPanel>
    </>
  );
};

export default SegmentPickerDialog;
